"use client";

import { FormEvent, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import CardNexusConnectPanel from "@/components/CardNexusConnectPanel";

export default function CompteForm() {
  const { user, loading, syncing, login, register, logout, syncNow } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, displayName || undefined);
      setOkMsg(
        mode === "login"
          ? "Connecté — données synchronisées."
          : "Compte créé — ta collection locale a été envoyée en ligne.",
      );
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-gray-400">
        Chargement du compte…
      </div>
    );
  }

  if (user) {
    return (
      <div className="mx-auto max-w-lg px-4 py-10 sm:px-6">
        <p className="text-sm font-semibold uppercase tracking-wider text-cyan-400/80">Compte</p>
        <h1 className="mt-1 text-3xl font-black">
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Connecté
          </span>
        </h1>
        <div className="mt-6 space-y-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-sm text-gray-400">Email</p>
          <p className="font-semibold">{user.email}</p>
          {user.displayName && (
            <>
              <p className="pt-2 text-sm text-gray-400">Pseudo</p>
              <p className="font-semibold">{user.displayName}</p>
            </>
          )}
          <p className="pt-2 text-sm text-gray-500">
            Collection, decks et prépas sont synchronisés sur le serveur.
            {syncing ? " Synchronisation…" : ""}
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={syncing}
            onClick={async () => {
              setError(null);
              setOkMsg(null);
              try {
                await syncNow();
                setOkMsg("Synchronisation terminée.");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Sync échouée");
              }
            }}
            className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold hover:bg-cyan-500 disabled:opacity-50"
          >
            Forcer la sync
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded-xl border border-white/15 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/5"
          >
            Se déconnecter
          </button>
        </div>
        {okMsg && <p className="mt-4 text-sm text-emerald-400">{okMsg}</p>}
        {error && <p className="mt-4 text-sm text-rose-400">{error}</p>}

        <CardNexusConnectPanel returnTo="/compte" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-10 sm:px-6">
      <p className="text-sm font-semibold uppercase tracking-wider text-cyan-400/80">Compte</p>
      <h1 className="mt-1 text-3xl font-black">
        <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
          {mode === "login" ? "Connexion" : "Créer un compte"}
        </span>
      </h1>
      <p className="mt-2 text-sm text-gray-500">
        Stocke ta collection, tes decks et prépas en ligne pour les retrouver partout.
      </p>

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={() => setMode("login")}
          className={`rounded-xl px-3 py-1.5 text-sm font-medium ${
            mode === "login" ? "bg-white/10 text-white" : "text-gray-400 hover:bg-white/5"
          }`}
        >
          Connexion
        </button>
        <button
          type="button"
          onClick={() => setMode("register")}
          className={`rounded-xl px-3 py-1.5 text-sm font-medium ${
            mode === "register" ? "bg-white/10 text-white" : "text-gray-400 hover:bg-white/5"
          }`}
        >
          Inscription
        </button>
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        {mode === "register" && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-400">Pseudo (optionnel)</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-500/50"
              autoComplete="nickname"
            />
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-400">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-500/50"
            autoComplete="email"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-400">Mot de passe</span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-500/50"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />
        </label>
        {error && <p className="text-sm text-rose-400">{error}</p>}
        {okMsg && <p className="text-sm text-emerald-400">{okMsg}</p>}
        <button
          type="submit"
          disabled={busy || syncing}
          className="w-full rounded-xl bg-cyan-600 py-2.5 text-sm font-bold hover:bg-cyan-500 disabled:opacity-50"
        >
          {busy || syncing
            ? "…"
            : mode === "login"
              ? "Se connecter"
              : "Créer mon compte"}
        </button>
      </form>

    </div>
  );
}
